// ==================== RESELLER DASHBOARD ====================
// The reseller's only screen: read-only commission totals, its coupons, the customers
// that used them (business name and plan only) and the commission rows.

import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle, Clock, RefreshCw, Wallet } from 'lucide-react';
import { Badge, Button, Card, CardHeader, CardTitle } from '../../components/ui';
import { resellerService, type ResellerDashboard } from '../../services/reseller.service';
import { CommissionTable } from '../../components/resellers/CommissionTable';
import { CommissionAmount } from '../../components/resellers/CommissionAmount';
import {
  discountScopeLabel,
  durationLabel,
  eligibilityLabel,
  formatExpiryDate,
  formatPercent,
  isCouponExpired,
} from '../../components/resellers/labels';
import { formatCurrency, formatDate } from '../../utils/helpers';

const totalCards = [
  { field: 'earned', title: 'Earned', caption: 'All commission so far', icon: Wallet },
  { field: 'pending', title: 'Pending', caption: 'Not paid out yet', icon: Clock },
  { field: 'paid', title: 'Paid', caption: 'Paid out to you', icon: CheckCircle },
] as const;

const MoneyLines = ({ rows }: { rows: Array<{ currency: string; amount: number }> }) =>
  rows.length ? (
    <>
      {rows.map((row) => (
        <span className="block" key={row.currency}>
          {formatCurrency(Number(row.amount), row.currency)}
          {rows.length > 1 && <span className="text-xs text-(--color-muted-gray)"> {row.currency}</span>}
        </span>
      ))}
    </>
  ) : (
    <>—</>
  );

export const ResellerDashboardPage = () => {
  const [dashboard, setDashboard] = useState<ResellerDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    const request = ++latestRequest.current;
    const response = await resellerService.getDashboard();
    if (request !== latestRequest.current) return;
    if (response.success && response.data) {
      setDashboard(response.data);
      setError(null);
    } else {
      setError(response.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const requestState = latestRequest;
    void Promise.resolve().then(load);
    return () => {
      requestState.current++;
    };
  }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white">Commission Dashboard</h1>
          {dashboard && (
            <p className="text-sm text-(--color-muted-gray) mt-1">
              {dashboard.profile.name}
              {dashboard.profile.companyName && ` · ${dashboard.profile.companyName}`}
            </p>
          )}
        </div>
        <Button variant="secondary" onClick={() => void load()} leftIcon={<RefreshCw size={16} />}>
          Refresh
        </Button>
      </div>

      {error && (
        <p role="alert" className="text-sm text-(--color-error)">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-(--color-muted-gray)">Loading dashboard...</p>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-4">
            {totalCards.map(({ field, title, caption, icon: Icon }) => (
              <Card key={field}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm text-(--color-muted-gray)">{title}</p>
                    <div className="mt-1 text-2xl font-bold text-white font-heading">
                      <CommissionAmount totals={dashboard?.totals} field={field} />
                    </div>
                    <p className="text-xs text-(--color-muted-gray) mt-2">{caption}</p>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-(--color-signal-orange)/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-(--color-signal-orange)" />
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Your coupons</CardTitle>
            </CardHeader>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-(--color-muted-gray) border-b border-(--color-border-gray)">
                  <tr>
                    {['Code', 'Customer discount', 'Your commission', 'Customers', 'Expiry', 'Status'].map((text) => (
                      <th key={text} className="p-3 font-medium whitespace-nowrap">
                        {text}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-(--color-border-gray)">
                  {dashboard?.coupons.map((coupon) => (
                    <tr key={coupon.id} className="text-(--color-soft-gray)">
                      <td className="p-3">
                        <span className="text-white font-medium">{coupon.code}</span>
                        <span className="block text-xs text-(--color-muted-gray)">
                          {eligibilityLabel(coupon.allowExistingCustomers)}
                          {coupon.appliesToOfflinePayments && ' · admin-recorded payments too'}
                        </span>
                      </td>
                      <td className="p-3">
                        {formatPercent(coupon.discountPercent)}
                        <span className="block text-xs text-(--color-muted-gray)">
                          {discountScopeLabel(coupon.discountScope)} · {durationLabel(coupon.discountDuration)}
                        </span>
                      </td>
                      <td className="p-3">
                        {formatPercent(coupon.commissionPercent)}
                        <span className="block text-xs text-(--color-muted-gray)">
                          {durationLabel(coupon.commissionDuration)}
                        </span>
                      </td>
                      <td className="p-3 whitespace-nowrap">
                        {coupon.uses} / {coupon.maxUses ?? 'Unlimited'}
                        <span className="block text-xs text-(--color-muted-gray)">
                          {coupon.currentCustomers} current
                        </span>
                      </td>
                      <td className="p-3 whitespace-nowrap">{formatExpiryDate(coupon.expiryDate)}</td>
                      <td className="p-3">
                        <Badge variant={coupon.isActive && !isCouponExpired(coupon) ? 'success' : 'default'} size="sm">
                          {coupon.isActive ? (isCouponExpired(coupon) ? 'Expired' : 'Active') : 'Inactive'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {!dashboard?.coupons.length && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-(--color-muted-gray)">
                        No coupons yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Your customers</CardTitle>
            </CardHeader>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-(--color-muted-gray) border-b border-(--color-border-gray)">
                  <tr>
                    {[
                      'Business',
                      'Current plan',
                      'Coupon',
                      'First use',
                      'Commissioned payments',
                      'Your commission',
                      'Status',
                    ].map((text) => (
                      <th key={text} className="p-3 font-medium whitespace-nowrap">
                        {text}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-(--color-border-gray)">
                  {dashboard?.customers.map((customer) => (
                    <tr key={customer.organizationId} className="text-(--color-soft-gray)">
                      <td className="p-3 text-white">{customer.businessName}</td>
                      <td className="p-3">{customer.plan || '—'}</td>
                      <td className="p-3">{customer.couponCode}</td>
                      <td className="p-3 whitespace-nowrap">{formatDate(customer.firstUseDate)}</td>
                      <td className="p-3 whitespace-nowrap">
                        <MoneyLines
                          rows={customer.byCurrency.map((row) => ({ currency: row.currency, amount: row.totalPaid }))}
                        />
                      </td>
                      <td className="p-3 whitespace-nowrap">
                        <MoneyLines
                          rows={customer.byCurrency.map((row) => ({ currency: row.currency, amount: row.commission }))}
                        />
                      </td>
                      <td className="p-3">
                        <Badge variant={customer.isCurrent ? 'success' : 'default'} size="sm">
                          {customer.isCurrent ? 'Uses your coupon' : 'Ended'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {!dashboard?.customers.length && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-(--color-muted-gray)">
                        No customers yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Commissions</CardTitle>
        </CardHeader>
        <CommissionTable loadCommissions={resellerService.getCommissions} />
      </Card>
    </div>
  );
};
